import { useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useStoreState, Session } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Layout, ToolbarItem } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";

import { Box, Popover, Tooltip, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EmailIcon from "@mui/icons-material/Email";
import OpacityIcon from "@mui/icons-material/Opacity";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import BuildIcon from "@mui/icons-material/Build";
import DashboardIcon from "@mui/icons-material/Dashboard";

import { faTint } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useRequest from "components/hooks/useRequest";

import { DataFormat, hasPluginPermission } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import { WaterLevel } from "types/index";
import { GetLastGasoilPriceDocument } from "__generated__/view/gasoilprices/graphql/queries.generated";
import CoastguardProfileToolbarItem from "components/layout/coastguard/CoastguardProfileToolbarItem";
import useWindowSize, { LayoutModes } from "@cofano/cck/hooks/useWindowSize";

const LayoutTop = () => {

	const navigate = useNavigate();
	const session = useStoreState(store => store.session);

	const [ windowSize ] = useWindowSize();

	const { user } = session;

	const { data: lastGasoilPriceData } = useQuery(GetLastGasoilPriceDocument, {
		skip: !hasPluginPermission(session, "gasoilPrice"),
	});

	const lastGasoilPrice = lastGasoilPriceData?.lastGasoilPrice;

	const [ insystemnotificationsUnread ] = useRequest({
		defaultResponse: 0,
		url: "insystemnotifications/unread/amount",
		allowed: hasPluginPermission(session, "messages"),
		interval: 60000,
	});

	const [ insystemnotifications ] = useRequest<{ results: { id: number }[] } | undefined>({
		defaultResponse: undefined,
		url: "insystemnotifications/inbox?start=0&rows=10",
		allowed: hasPluginPermission(session, "messages"),
		interval: 60000,
	});

	const [ waterlevel, { trigger: triggerWaterLevel } ] = useRequest<WaterLevel | undefined>({
		defaultResponse: undefined,
		url: `waterlevels/measuring-points/${user?.properties.userPreferences?.defaultWaterLevel ?? ""}`,
		allowed: (user?.properties.userPreferences?.defaultWaterLevel as boolean | undefined) ?? false,
	});

	const [ notificationsAnchorEl, setNotificationsAnchorEl ] = useState<HTMLElement | null>(null);

	usePropertyChangeEffect(() => {
		if (user?.properties.userPreferences?.defaultWaterLevel) {
			triggerWaterLevel();
		}
	}, [ user?.properties.userPreferences?.defaultWaterLevel ]);

	return (
		<Layout.Toolbar
			profileSettings={windowSize === LayoutModes.Mobile ? {
				label: user?.displayName ?? "",
				items: [ {
					icon: <AccountCircleIcon />,
					label: intl.formatMessage({ id: "NAV_USER_DETAILS" }),
					url: `/view/users/${user?.properties.id}/details`,
				}, {
					icon: <DashboardIcon />,
					label: intl.formatMessage({ id: "COASTGUARD" }),
					onClick: () => window.open(session.settings?.settings.coastguardUrl, "_blank"),
				}, {
					icon: <SettingsIcon />,
					label: intl.formatMessage({ id: "USER_NOTIFICATION_SETTINGS" }),
					url: "/view/notifications/settings",
				} ],
				logoutSettings: {
					label: intl.formatMessage({ id: "NAV_LOGOUT" }),
					onClick: () => {
						navigate("/view/logout");
					},
				},
			} : undefined}
		>
			{hasPluginPermission(session, "gasoilPrice") && lastGasoilPrice && (
				<ToolbarItem
					icon={<FontAwesomeIcon icon={faTint} style={{ height: 16, width: 16 }} />}
					label={DataFormat.price(lastGasoilPrice?.price)} />
			)}

			{waterlevel && (
				<ToolbarItem
					icon={<OpacityIcon />}
					label={`${DataFormat.waterLevel(waterlevel.waterLevelCm)} - ${waterlevel.name}`}
					url={"/view/maps?tab=waterlevels"} />
			)}

			{hasPluginPermission(session, "messages") && insystemnotificationsUnread > 0 && (
				<ToolbarItem
					icon={<NotificationsIcon />}
					badge={insystemnotificationsUnread}
					onClick={event => setNotificationsAnchorEl(event.currentTarget)} />
			)}

			{hasPluginPermission(session, "messages") && insystemnotificationsUnread < 1 && (
				<ToolbarItem
					icon={<NotificationsIcon />}
					url="/view/notifications" />
			)}

			<Tooltip title={intl.formatMessage({ id: "USER_NOTIFICATION_SETTINGS" })}>
				<span style={{ display: "flex", alignItems: "center" }}>
					<ToolbarItem
						icon={<SettingsIcon />}
						onClick={() => navigate("/view/notifications/settings")}
					/>
				</span>
			</Tooltip>

			{hasPluginPermission(session, "supportTopMenu") && (
				<TopMenuSupport session={session} />
			)}

			<Notifications
				anchorEl={notificationsAnchorEl}
				onClose={() => setNotificationsAnchorEl(null)}
				notifications={insystemnotifications?.results || []}
				navigate={navigate}
			/>

			<div style={{ order: 1 }}>
				<CoastguardProfileToolbarItem
					onProfileClick={() => navigate(`/view/users/${user?.properties.id}/details`)}
				/>
			</div>

		</Layout.Toolbar>
	);
};

const TopMenuSupport = ({ session }: { session: Session }) => {
	const supportTicketSystemUrl = session.settings?.settings?.supportTicketSystemUrl;
	const supportTicketSystemUrlLabel = session.settings?.settings?.supportTicketSystemUrlLabel;
	const showSupportTicketSystemUrl = hasPluginPermission(session, "supportShowTicketSystem") && supportTicketSystemUrl;
	const supportSettingsPhoneNumber = session.settings?.multiValues.supportPhoneNumbers?.split(";");
	const supportSettingsEmail = session.settings?.settings?.supportEmail;
	const showSupportPhoneNumber = hasPluginPermission(session, "supportShowPhoneNumbers") && supportSettingsPhoneNumber;
	const showSupportEmail = hasPluginPermission(session, "supportShowEmail") && supportSettingsEmail;

	/*const items = [ {
		label: intl.formatMessage({ id: "SUPPORT" }),
		onClick: () => window.open(supportUrl, "_blank"),
		icon: <HelpIcon />
	} ];*/

	const items = [];

	if ( showSupportTicketSystemUrl)	{
		items.push({
			label: supportTicketSystemUrlLabel,
			onClick: () => window.open(supportTicketSystemUrl, "_blank"),
			icon: <BuildIcon />,
		});
	}
	if ( showSupportEmail)	{
		items.push({
			label: supportSettingsEmail,
			icon: <EmailIcon />,
		});
	}
	if (showSupportPhoneNumber) {
		supportSettingsPhoneNumber.forEach(p => {
			items.push({
				label: p,
				icon: <PhoneIcon />,
			});
		});
	}

	return (
		<ToolbarItem
			icon={<InfoOutlinedIcon />}
			items={items} />);
};

const Notifications = ({
	anchorEl,
	onClose,
	notifications,
	navigate,
}: {
	anchorEl: HTMLElement | null;
	onClose: () => void;
	notifications: {
		id: number;
		sender?: {
			username: string;
		};
		read?: boolean;
		createdOn?: number;
		subject?: string;
	}[]
	navigate: NavigateFunction;
}) => {
	return (
		<Popover
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			onClose={onClose}
			slotProps={{
				paper: {
					sx: {
						borderRadius: 0,
						top: "50px !important",
					},
				},
			}}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "right",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "right",
			}}>
			<Box sx={{ width: 300 }}>

				{[ ...notifications ].slice(0, 3).map((item, index) => {
					const sender = item.sender?.username || intl.formatMessage({ id: "NOTIFICATION_SYSTEM_SENDER" });
					const href = `/view/notifications?id=${item.id}`;

					return (
						<Box
							key={item.id || index}
							onClick={() => {
								navigate(href);
								onClose();
							}}
							sx={{
								backgroundColor: item.read ? "transparent" : "action.hover",
								borderBottom: "1px solid",
								borderColor: "divider",
								cursor: "pointer",
								display: "flex",
								flexDirection: "column",
								gap: 0.5,
								p: 1.5,
								transition: "background-color 0.2s ease",

								"&:hover": {
									backgroundColor: "action.selected",
								},
							}}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexGrow: 1 }}>
									{!item.read && (
										<Box
											sx={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												backgroundColor: "primary.main",
												flexShrink: 0,
											}} />
									)}

									<Typography
										variant="subtitle2"
										sx={{
											fontWeight: item.read ? 500 : 700,
											fontSize: 13,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}>
										{sender}
									</Typography>
								</Box>

								<Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pl: 1 }}>
									<FormattedDate date={item.createdOn} withoutYear />
								</Typography>
							</Box>

							<Typography
								variant="body2"
								color={item.read ? "text.secondary" : "text.primary"}
								sx={{
									fontWeight: item.read ? 400 : 500,
									fontSize: 13,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{item.subject}
							</Typography>
						</Box>
					);
				})}

				<Box
					onClick={() => navigate("/view/notifications")}
					sx={{
						alignItems: "center",
						cursor: "pointer",
						display: "flex",
						justifyContent: "center",
						p: 0.5,
						transition: "background-color 0.2s ease",

						"&:hover": {
							color: "#1890ff",
						},
					}}>
					<MoreHorizIcon />
				</Box>
			</Box>
		</Popover>
	);
};

export default LayoutTop;
